import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { BadgeCheck, Star, Package } from 'lucide-react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { getAvatarUrl } from '@/utils/avatar'
import type { UserProfile } from '@/types/user'

interface StorefrontPreviewCardProps {
  sellerId: string
  sellerName: string
  verified?: boolean
  rating?: number
  reviewCount?: number
  listingCount?: number
  location?: string
  listingType?: 'general' | 'vehicle' | 'fundraiser'
}

export function StorefrontPreviewCard({
  sellerId,
  sellerName,
  verified,
  rating,
  reviewCount,
  listingCount,
  location,
  listingType,
}: StorefrontPreviewCardProps) {
  const [sellerProfile, setSellerProfile] = useState<UserProfile | null>(null)

  useEffect(() => {
    getDoc(doc(db, 'users', sellerId)).then((snap) => {
      if (snap.exists()) setSellerProfile({ uid: snap.id, ...snap.data() } as UserProfile)
    })
  }, [sellerId])

  const avatarSrc = sellerProfile ? getAvatarUrl(sellerProfile) : null
  const initials = sellerName?.[0]?.toUpperCase() || '?'
  // Prefer slug-based URL when available
  const storeUrl = sellerProfile?.storeSlug
    ? `/store/${sellerProfile.storeSlug}`
    : `/seller/${sellerId}`

  return (
    <Link
      to={storeUrl}
      className="card p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors"
    >
      {/* Avatar */}
      <div className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center text-lg font-bold flex-shrink-0 overflow-hidden">
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={sellerName}
            className="w-full h-full object-contain bg-white"
          />
        ) : (
          initials
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium truncate">{sellerName}</span>
          {verified && <BadgeCheck className="w-4 h-4 text-primary-500 flex-shrink-0" />}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
          {rating !== undefined && rating > 0 && (
            <span className="flex items-center gap-0.5">
              <Star className="w-3 h-3 text-primary-500 fill-primary-500" />
              {rating.toFixed(1)}
              {reviewCount !== undefined && ` (${reviewCount})`}
            </span>
          )}
          {listingCount !== undefined && (
            <span className="flex items-center gap-0.5">
              <Package className="w-3 h-3" />
              {listingCount} listings
            </span>
          )}
          {location && <span>{location}</span>}
        </div>
      </div>

      <span className="text-xs text-primary-600 font-medium flex-shrink-0">
        {sellerProfile?.subscriptionTier === 'dealership'
          ? 'View Showroom'
          : listingType === 'fundraiser'
            ? 'View Fundraiser'
            : sellerProfile?.subscriptionTier === 'storefront'
              ? 'View Store'
              : 'View Listing'}
      </span>
    </Link>
  )
}
